import { createPlugin } from '@customerio/dryad';
import { isRecord } from '@/utils/objects';
import { ConditionType } from './condition-types';
import type { SegAttrFilter } from './wire-types';

/**
 * The attribute-trigger campaign's "who matches my trigger" pointer:
 * `{ seg_attr: { segment_id, campaign_id } }`. The trigger's actual conditions
 * live in the campaign's backing segment (the backend evaluates the leaf as
 * plain membership of `segment_id` — SegAttrExpr in services'
 * libraries/filters/expr.go), so the wire carries only the two ids and there
 * is nothing here for a person to edit. No editor writes it either: it only
 * enters through the trigger panel's "matches" deep link. The row is
 * therefore read-only — this plugin exists so the saved condition renders as
 * the sentence legacy showed ("Trigger in <campaign>") instead of the
 * unclaimed-leaf "can't be edited" fallback.
 */
export type SegAttrConditionState = {
  segmentId: number | null;
  campaignId: number | null;
};

// Both ids must be numbers, mirroring the backend's parseSegAttr (services'
// libraries/filters/json.go), which rejects anything else. The filters param
// is user-editable URL data, so a malformed leaf declines to the ErrorNode
// passthrough rather than claiming a wire the backend won't serve.
function parseSegAttr(json: unknown): SegAttrConditionState | null {
  if (!isRecord(json) || !isRecord(json.seg_attr)) {
    return null;
  }
  const { segment_id, campaign_id } = json.seg_attr;
  if (typeof segment_id !== 'number' || typeof campaign_id !== 'number') {
    return null;
  }
  return { segmentId: segment_id, campaignId: campaign_id };
}

// Key order matches the legacy serializer, so the stringified filters param
// round-trips byte-identically.
function serializeSegAttr(state: SegAttrConditionState): SegAttrFilter {
  return {
    seg_attr: { segment_id: state.segmentId ?? '', campaign_id: state.campaignId ?? '' },
  };
}

export const peopleFilterSegAttrPlugin = createPlugin({
  type: ConditionType.SegAttr,
  label: 'Campaign trigger',
  parse: parseSegAttr,
  serialize: serializeSegAttr,
  // Null ids exist only via createDefault, which is unreachable — the type is
  // never offered in the add menu. Should that ever change, the invalid
  // default keeps a half-built row off the wire.
  validate: (state: SegAttrConditionState) =>
    state.segmentId == null || state.campaignId == null ? [{ message: 'This condition is missing its campaign.' }] : [],
  createDefault: (): SegAttrConditionState => ({ segmentId: null, campaignId: null }),
});
